import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Auth, Hub } from 'aws-amplify';
import { useToast } from '@pulsarml/avalon-ui';
import { I18n } from '@utils/i18n';
import awsmobile, { customProvider } from '../../../aws-exports';
import { Logger } from '_clean-code/utils/logger';
import { authorizeByToken } from '@utils/amplify/authorizeByToken';
import { useAuthListener } from './useAuthListener';
import { getMicrosoftEntraProvider } from '../microsoftEntra';
import posthog from 'posthog-js';
import {
  EAuthChannel,
  setAuthChannel
} from '_clean-code/analytics/authChannel';
import { clearAppStorage } from '_clean-code/utils/storage';

interface ILoginCredentials {
  email: string;
  password: string;
}

/** Thrown when the provider lookup answers `{provider: null}`. */
class NoEntraProviderError extends Error {
  constructor() {
    super('MICROSOFT_LOGIN_UNAVAILABLE');
    this.name = 'NoEntraProviderError';
  }
}

export const useAuth = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isAuthenticated, isLoading } = useAuthListener();

  const login = useMutation({
    mutationFn: async (credentials: ILoginCredentials) => {
      setAuthChannel(EAuthChannel.Email);
      const user = await Auth.signIn(credentials.email, credentials.password);
      return user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast({
        variant: 'success',
        title: I18n.t('successfully_logged')
      });
    },
    onError: (error) => {
      Logger.error('useAuth::login', error);
      toast({
        variant: 'error',
        title: `Error ${error.message}`
      });
    }
  });

  const loginWithOkta = useMutation({
    mutationFn: async () => {
      setAuthChannel(EAuthChannel.Okta);
      await Auth.federatedSignIn({
        customProvider
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
    onError: (error) => {
      Logger.error('useAuth::loginWithOkta', error);
      toast({
        variant: 'error',
        title: `Error ${error.message}`
      });
    }
  });

  const loginWithMicrosoft = useMutation({
    mutationFn: async (email: string) => {
      const provider = await getMicrosoftEntraProvider(email);
      if (!provider) {
        // Backend returns null for every unavailable case, so we never leak
        // whether the account/tenant exists.
        throw new NoEntraProviderError();
      }
      setAuthChannel(EAuthChannel.Entra);
      // Redirects to the customer's Entra tenant, then back to the OAuth
      // callback handled below (same path as Okta).
      await Auth.federatedSignIn({ customProvider: provider });
    },
    onError: (error) => {
      if (error instanceof NoEntraProviderError) {
        // Expected outcome, not an exception.
        Logger.info('useAuth::loginWithMicrosoft::noEntraProvider');
        toast({
          variant: 'info',
          title: I18n.t('microsoft_login_not_enabled')
        });
        return;
      }

      Logger.error('useAuth::loginWithMicrosoft', error);
      toast({
        variant: 'error',
        title: I18n.t('microsoft_login_failed')
      });
    }
  });

  const logout = useMutation({
    mutationFn: async () => {
      await Auth.signOut();
      clearAppStorage({ keepAnalyticsIdentity: false });
      // this causes a full page reload, so the redux state is effectively cleared
      window.location.href = '/login';
    },
    onSuccess: () => {
      // note: this will clear the cache for all queries
      queryClient.clear();
      posthog.reset();
      Sentry.setContext('user_context', null);
      Sentry.setUser(null);
      Logger.info('useAuth::logout::success');
    },
    onError: (error) => {
      Logger.error('useAuth::logout', error);
      toast({
        variant: 'error',
        title: error.message
      });
    }
  });

  const deleteAccount = useMutation({
    mutationFn: async () => {
      await Auth.deleteUser();
      clearAppStorage({ keepAnalyticsIdentity: false });
      window.location.href = '/login';
    },
    onSuccess: () => {
      // note: this will clear the cache for all queries
      queryClient.clear();
      posthog.reset();
      Logger.info('useAuth::deleteAccount::success');
      toast({
        variant: 'success',
        title: I18n.t('account_deleted_successfully')
      });
    },
    onError: (error) => {
      Logger.error('useAuth::deleteAccount', error);
      toast({
        variant: 'error',
        title: `Error ${error.message}`
      });
    }
  });

  const fragments = new URLSearchParams(window.location.hash.replace('#', ''));
  const idToken = fragments.get('id_token');
  const accessToken = fragments.get('access_token');
  if (accessToken) {
    Logger.info('start authorizeByToken');
    authorizeByToken(
      {
        idToken: idToken ?? '',
        accessToken,
        refreshToken: ''
      },
      {
        userPoolId: awsmobile.aws_user_pools_id,
        clientId: awsmobile.aws_user_pools_web_client_id
      }
    ).then((cognitoUser) => {
      Logger.info('end authorizeByToken');
      window.location.replace('#');
      // slice off the remaining '#' in HTML5:
      if (typeof window.history.replaceState == 'function') {
        history.replaceState({}, '', window.location.href.slice(0, -1));
      }
      // authorizeByToken sets up the session manually without going through
      // Auth.signIn, so Amplify won't fire a signIn Hub event automatically.
      Hub.dispatch('auth', { event: 'signIn', data: cognitoUser });
    });
  }

  return {
    login,
    logout,
    loginWithOkta,
    loginWithMicrosoft,
    deleteAccount,
    isAuthenticated,
    isLoading
  };
};
